import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { PrestadorAtividadesService } from '@/core/api/sicom-api/registro/prestador/prestador-atividades.service';
import { PrestadorAtividade, PrestadorCnae } from '@/core/api/sicom-api/registro/prestador/prestador-atividades.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-atividades-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, BotaoAcoes, Button, CardContent, Checkbox, ConfirmDialog, CustomTableComponent, Dialog, Select, TabContainer, TabContent],
    providers: [ConfirmationService],
    templateUrl: './atividades-prestador.html',
    styleUrl: './atividades-prestador.scss'
})
export class AtividadesPrestador {
    pessoa = input<Pessoa>();

    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorAtividadesService);
    private readonly confirmation = inject(ConfirmationService);
    private readonly notificacoes = inject(NotificacoesService);
    readonly cnaes = signal<PrestadorCnae[]>([]);
    readonly cnaesTabela = computed(() =>
        this.cnaes()
            .slice()
            .sort((a, b) => {
                // Atividade principal sempre na primeira linha; as demais em ordem alfabética.
                const ordemA = a.principal === 'Principal' ? 0 : 1;
                const ordemB = b.principal === 'Principal' ? 0 : 1;
                return ordemA !== ordemB ? ordemA - ordemB : (a.descricao ?? '').localeCompare(b.descricao ?? '');
            })
            .map((item) => ({
                ...item,
                tipo: { value: item.principal, severity: item.principal === 'Principal' ? 'success' : 'secondary' }
            }))
    );
    // Coloração da linha (espelha o legado DbgCNAEDrawColumnCell): verde = cadastrado manualmente;
    // vermelho = sem indicativo de área médica; padrão = sem cor.
    readonly rowStyleCnae = (row: { cadastradoManual?: boolean; semIndicativoAreaMedica?: boolean }): Record<string, string> => {
        if (row.cadastradoManual) return { color: '#16a34a' };
        if (row.semIndicativoAreaMedica) return { color: '#dc2626' };
        return {};
    };
    readonly atividades = signal<PrestadorAtividade[]>([]);
    readonly atividadesTabela = computed(() => this.atividades().map((item) => ({ ...item, dtInclusao: DateUtils.formatDate(item.dtInclusao), dtExclusao: DateUtils.formatDate(item.dtExclusao) })));
    readonly opcoesCnae = signal<PrestadorCnae[]>([]);
    readonly opcoesAtividade = signal<PrestadorAtividade[]>([]);
    readonly carregando = signal(false);
    readonly salvando = signal(false);
    readonly carregandoNuvem = signal(false);
    selected = 'CNAE';
    dialogCnae = false;
    dialogAtividade = false;
    readonly options = [
        { label: 'CNAE', value: 'CNAE', icon: 'pi pi-briefcase' },
        { label: 'Atividades Desenvolvidas', value: 'ATIVIDADES', icon: 'pi pi-briefcase' }
    ];
    readonly formCnae = this.fb.group({ idCnae: this.fb.control<string | null>(null, Validators.required), principal: this.fb.control(false) });
    readonly formAtividade = this.fb.group({ idAtividade: this.fb.control<number | null>(null, Validators.required) });
    protected readonly roleEditar = PERMISSOES.prestadores.atividades.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({ id: 'novo', label: 'Novo', icon: 'pi pi-plus', cor: 'novo', permission: this.roleEditar, command: () => (this.selected === 'CNAE' ? this.novoCnae() : this.novoAtividade()) }),
            acaoMenuItem({
                id: 'carregar-nuvem',
                label: 'Carregar da nuvem',
                icon: 'pi pi-cloud-download',
                cor: 'info',
                disabled: this.carregandoNuvem() || this.selected !== 'CNAE',
                permission: this.roleEditar,
                command: () => this.carregarCnaesNuvem()
            }),
            acaoMenuItem({ id: 'atualizar', label: 'Atualizar', icon: 'pi pi-refresh', cor: 'info', disabled: this.carregando(), command: () => this.carregar() })
        ];
    }

    readonly metadataCnae: TableMetadata = {
        headers: {
            idCnae: { type: 'text', value: 'Código', default: '-', hidden: false },
            descricao: { type: 'text', value: 'Atividade Econômica', default: '-', hidden: false },
            tipo: { type: 'tag', value: 'Tipo', default: '-', hidden: false }
        },
        title: 'descricao',
        actions: { remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: PrestadorCnae) => this.removerCnae(item), permission: this.roleEditar } },
        defaultSortField: 'descricao',
        defaultSortOrder: 1
    };
    readonly metadataAtividade: TableMetadata = {
        headers: {
            descricao: { type: 'text', value: 'Atividade', default: '-', hidden: false },
            dtInclusao: { type: 'text', value: 'Inclusão', default: '-', hidden: true },
            dtExclusao: { type: 'text', value: 'Exclusão', default: '-', hidden: true }
        },
        title: 'descricao',
        actions: { remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: PrestadorAtividade) => this.removerAtividade(item), permission: this.roleEditar } },
        defaultSortField: 'descricao',
        defaultSortOrder: 1
    };
    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa.uf) this.carregar();
        });
    }
    carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.carregando.set(true);
        this.service
            .listarCnaes(pessoa.id, pessoa.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (itens) => this.cnaes.set(itens.map((i) => ({ ...i, principal: i.principal ? 'Principal' : 'Secundária' }))),
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.cnaes.set([])
            });
        this.service
            .listar(pessoa.id, pessoa.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (itens) => {
                    this.atividades.set(itens);
                    this.carregando.set(false);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => {
                    this.atividades.set([]);
                    this.carregando.set(false);
                }
            });
    }
    novoCnae(): void {
        this.formCnae.reset({ principal: false });
        this.service
            .opcoesCnae()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((itens) => this.opcoesCnae.set(itens));
        this.dialogCnae = true;
    }
    novoAtividade(): void {
        this.formAtividade.reset();
        this.service
            .opcoes()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((itens) => this.opcoesAtividade.set(itens));
        this.dialogAtividade = true;
    }
    salvarCnae(): void {
        const pessoa = this.pessoa();
        if (!pessoa || this.formCnae.invalid) return;
        this.salvando.set(true);
        this.service
            .criarCnae({ id: pessoa.id, uf: pessoa.uf, idCnae: this.formCnae.value.idCnae!, principal: !!this.formCnae.value.principal })
            .pipe(takeUntilDestroyed(this.destroyRef))
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.dialogCnae = false;
                this.salvando.set(false);
                this.carregar();
                this.notificacoes.notificar({ severity: 'success', summary: 'CNAE salvo.' });
            });
    }
    salvarAtividade(): void {
        const pessoa = this.pessoa();
        if (!pessoa || this.formAtividade.invalid) return;
        this.salvando.set(true);
        this.service
            .criar({ id: pessoa.id, uf: pessoa.uf, idAtividade: this.formAtividade.value.idAtividade! })
            .pipe(takeUntilDestroyed(this.destroyRef))
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.dialogAtividade = false;
                this.salvando.set(false);
                this.carregar();
                this.notificacoes.notificar({ severity: 'success', summary: 'Atividade salva.' });
            });
    }
    carregarCnaesNuvem(): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.confirmation.confirm({
            header: 'Carregar CNAEs da nuvem',
            message: 'Buscar os CNAEs oficiais deste CNPJ na nuvem (Receita)? Os CNAEs retornados serão adicionados e os CNAEs atuais que não constarem na nuvem serão removidos.',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.carregandoNuvem.set(true);
                this.service
                    .atualizarCnaesNuvem(pessoa.id, pessoa.uf)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        finalize(() => this.carregandoNuvem.set(false))
                    )
                    // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
                    .subscribe((resultado) => {
                        this.carregar();
                        const partes = [`${resultado.adicionados} adicionado(s)`, `${resultado.removidos} removido(s)`];
                        if (resultado.ignorados?.length) partes.push(`${resultado.ignorados.length} ignorado(s) (fora da referência)`);
                        this.notificacoes.notificar({
                            severity: resultado.ignorados?.length ? 'warn' : 'success',
                            summary: 'CNAEs sincronizados',
                            detail: partes.join(' · ')
                        });
                    });
            }
        });
    }
    removerCnae(item: PrestadorCnae): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.confirmation.confirm({
            message: `Remover CNAE ${item.idCnae}?`,
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () =>
                this.service
                    .removerCnae({
                        id: pessoa.id,
                        uf: pessoa.uf,
                        idCnae: item.idCnae,
                        principal: item.principal === 'Principal'
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
                    .subscribe(() => this.carregar())
        });
    }
    removerAtividade(item: PrestadorAtividade): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.confirmation.confirm({
            message: `Remover ${item.descricao}?`,
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () =>
                this.service
                    .remover({ id: pessoa.id, uf: pessoa.uf, idAtividade: item.idAtividade })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
                    .subscribe(() => this.carregar())
        });
    }
}
